import {md2html} from '@github-ui/md2html'
import {
  createMutationId,
  dispatchMutationLifecycleEvent,
  MutationLifecycleEvent,
  type ObjectPropertiesPatch,
  UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT,
} from '@github-ui/mutation-lifecycle-events'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {
  updateIssueCommentBodyMutation,
  updateIssueCommentBodyMutation$data,
  updateIssueCommentBodyMutation$rawResponse,
  UpdateIssueCommentInput,
} from './__generated__/updateIssueCommentBodyMutation.graphql'

function toObjectPropertiesPatch(data: updateIssueCommentBodyMutation$rawResponse): ObjectPropertiesPatch | undefined {
  const issueComment = data.updateIssueComment?.issueComment
  if (!issueComment) return undefined
  return {
    id: issueComment.id,
    values: {
      body: issueComment.body,
      bodyHTML: issueComment.bodyHTML,
      bodyVersion: issueComment.bodyVersion,
      viewerCanUpdate: issueComment.viewerCanUpdate,
    },
  }
}

export function commitUpdateIssueCommentBodyMutation({
  environment,
  input: {id, body, bodyVersion},
  onCompleted,
  onError,
  withOptimisticResponse = false,
}: {
  environment: Environment
  input: {id: string; body: string | null; bodyVersion: string}
  onCompleted?: (response?: updateIssueCommentBodyMutation$data) => void
  onError?: (error: Error) => void
  withOptimisticResponse?: boolean
}) {
  if (!body) {
    return
  }

  const inputHash: UpdateIssueCommentInput = {
    id,
    body,
    bodyVersion,
  }

  const optimisticBodyHTML = md2html(body)

  const optimisticResponse: updateIssueCommentBodyMutation$rawResponse | undefined = withOptimisticResponse
    ? {
        updateIssueComment: {
          issueComment: {
            id,
            body,
            bodyHTML: optimisticBodyHTML,
            bodyVersion,
            viewerCanUpdate: false,
          },
        },
      }
    : undefined

  const optimisticPatch = optimisticResponse ? toObjectPropertiesPatch(optimisticResponse) : undefined
  const optimisticEvent =
    optimisticPatch &&
    new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {phase: 'optimistic', data: optimisticPatch})
  const mutationId = optimisticEvent?.detail.mutationId ?? createMutationId()

  const disposable = commitMutation<updateIssueCommentBodyMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation updateIssueCommentBodyMutation($input: UpdateIssueCommentInput!) @raw_response_type {
        updateIssueComment(input: $input) {
          issueComment {
            id
            body
            bodyHTML(unfurlReferences: true)
            bodyVersion
            viewerCanUpdate
          }
        }
      }
    `,
    variables: {
      input: inputHash,
    },
    optimisticResponse,
    onCompleted: response => {
      const issueComment = response.updateIssueComment?.issueComment
      const patch =
        issueComment &&
        ({
          id: issueComment.id,
          values: {
            body: issueComment.body,
            bodyHTML: issueComment.bodyHTML,
            bodyVersion: issueComment.bodyVersion,
            viewerCanUpdate: issueComment.viewerCanUpdate,
          },
        } satisfies ObjectPropertiesPatch)
      if (patch) {
        dispatchMutationLifecycleEvent(
          new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {
            phase: 'completed',
            mutationId,
            data: patch,
          }),
        )
      }
      if (onCompleted) onCompleted(response)
    },
    onError: error => {
      dispatchMutationLifecycleEvent(
        new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {phase: 'error', mutationId, error}),
      )
      if (onError) onError(error)
    },
  })

  if (optimisticEvent) {
    dispatchMutationLifecycleEvent(optimisticEvent)
  }

  return disposable
}
